import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getIntlLocale } from "util/locale";
import KPILineChart from "./KPILineChart";
import KPIDoughnutChart from "./KPIDoughnutChart";
import styles from "pages/Welcome/style.module.scss";

/** Legacy metric-8 doughnut snapshots → review_statistics shape for hero + distribution. */
export function normalizeWelcomeReviewStatisticsPayload(data) {
  if (!data || typeof data !== "object") return null;
  if (data.chart_type === "review_statistics") return data;
  if (data.chart_type !== "doughnut" || !data.header) return null;
  return {
    chart_type: "review_statistics",
    header: data.header,
    distribution: {
      chart_type: "doughnut",
      labels: data.labels,
      datasets: data.datasets,
      legend: data.legend,
    },
    time_series: null,
  };
}

/**
 * Metric 8 — hero total + participation time series (+ optional platform doughnut).
 */
export default function WelcomeReviewStatisticsChart({ data, theme }) {
  const { t, i18n } = useTranslation();
  const nf = new Intl.NumberFormat(
    getIntlLocale(i18n.language),
  );

  const normalized = normalizeWelcomeReviewStatisticsPayload(data);
  const timeSeries = normalized?.time_series;
  const distribution = normalized?.distribution;
  const granularity = timeSeries?.granularity;

  const chartTimeSeries = useMemo(() => {
    if (!timeSeries?.labels?.length) return null;
    const locale = getIntlLocale(i18n.language);
    const fmt =
      granularity === "weekly" || granularity === "daily"
        ? new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" })
        : granularity === "monthly"
          ? new Intl.DateTimeFormat(locale, { month: "short", year: "numeric" })
          : null;
    if (!fmt) return timeSeries;
    return {
      ...timeSeries,
      labels: timeSeries.labels.map((label) => {
        const d = new Date(`${String(label).slice(0, 10)}T12:00:00`);
        return Number.isNaN(d.getTime()) ? label : fmt.format(d);
      }),
    };
  }, [timeSeries, granularity, i18n.language]);

  if (!normalized) {
    return null;
  }

  const total = Number(normalized.header?.value);
  const seriesLabel =
    granularity === "monthly"
      ? t("welcome.reviewStatsTimeSeriesMonthly")
      : granularity === "daily"
        ? t("welcome.reviewStatsTimeSeriesDaily", {
            defaultValue: t("welcome.reviewStatsTimeSeriesWeekly"),
          })
        : t("welcome.reviewStatsTimeSeriesWeekly");

  const distributionLegend =
    distribution?.legend && Array.isArray(distribution.legend)
      ? distribution.legend.map((item) => item.count)
      : null;

  return (
    <div className={styles.reviewStatisticsLayout}>
      {Number.isFinite(total) && (
        <div className={styles.reviewStatisticsHero}>
          <span className={styles.reviewStatisticsHeroLabel}>
            {t("welcome.reviewStatsTotalLabel")}
          </span>
          <span className={styles.reviewStatisticsHeroValue}>
            {nf.format(total)}
          </span>
          <span className={styles.reviewStatisticsHeroUnit}>
            {t("welcome.reviewStatsTotalUnit")}
          </span>
        </div>
      )}

      {chartTimeSeries?.labels?.length > 0 ? (
        <div className={styles.reviewStatisticsSeriesBlock}>
          <div className={styles.reviewStatisticsSeriesTitle}>{seriesLabel}</div>
          <div className={styles.reviewStatisticsSeriesChartWrap}>
            <KPILineChart
              data={chartTimeSeries}
              theme={theme}
              smooth
              hideLegend
              xLabelRotation={45}
            />
          </div>
        </div>
      ) : null}

      {distribution?.chart_type === "doughnut" &&
      distribution?.datasets?.[0]?.data?.length ? (
        <div className={styles.reviewStatisticsDistributionBlock}>
          <div className={styles.reviewStatisticsSeriesTitle}>
            {t("welcome.reviewStatsDistributionLabel")}
          </div>
          <KPIDoughnutChart
            data={distribution}
            theme={theme}
            customLabels={distributionLegend}
          />
        </div>
      ) : null}
    </div>
  );
}
